import { useEffect, useRef } from "react";

const pillars = [
  {
    icon: "ri-bank-line",
    roman: "II",
    title: "Financial Mechanisms & Institutional Systems",
    desc: "Structured understanding of banking systems, credit mechanisms, interest structures, taxation basics, and financial account architecture.",
    color: "bg-[#f0f9ff]",
    accent: "text-[#0284c7]",
    level: "Middle School",
  },
  {
    icon: "ri-line-chart-line",
    roman: "III",
    title: "Economic Systems & Market Dynamics",
    desc: "Macroeconomic fundamentals including inflation, labor markets, capital flows, supply-demand modeling, and policy-driven economic cycles.",
    color: "bg-[#eef2ff]",
    accent: "text-[#4338ca]",
    level: "High School",
  },
  {
    icon: "ri-stock-line",
    roman: "IV",
    title: "Capital, Risk & Asset Strategy",
    desc: "Investment principles, risk assessment, asset allocation frameworks, debt strategy, and long-term wealth-building models.",
    color: "bg-[#f0fdf4]",
    accent: "text-[#16a34a]",
    level: "High School+",
  },
  {
    icon: "ri-graduation-cap-line",
    roman: "V",
    title: "Post-Secondary Transition Architecture",
    desc: "Strategic navigation of student debt, compensation negotiation, tax optimization, credit positioning, and early-career financial infrastructure.",
    color: "bg-[#faf5ff]",
    accent: "text-[#7c3aed]",
    level: "Post-Secondary",
  },
];

export default function FrameworkSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll(".framework-card");
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = "1";
                (card as HTMLElement).style.transform = "translateY(0)";
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="framework" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center mb-6">
          <h2 className="font-['DM_Serif_Display'] text-4xl md:text-5xl text-[#0f172a] mt-3 leading-tight">
            The Financial Behavior Framework
          </h2>
          <p className="text-[#475569] text-sm md:text-base max-w-2xl mx-auto mt-4 leading-relaxed font-['Inter']">
            The Cash Learn Financial Behavior framework is structured as a developmentally progressive economic systems architecture spanning elementary foundations through post-secondary financial transition strategy.
          </p>
        </div>

        <div ref={sectionRef} className="flex flex-col gap-4 mt-12">
          {pillars.map((pillar, i) => (
            <div
              key={i}
              className={`framework-card ${pillar.color} rounded-2xl p-7 transition-all duration-500 hover:scale-[1.01] cursor-default border border-white flex flex-col md:flex-row md:items-center gap-5`}
              style={{ opacity: 0, transform: "translateY(24px)" }}
            >
              <div className="flex items-center gap-5 md:w-72 flex-shrink-0">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-white flex-shrink-0">
                  <i className={`${pillar.icon} text-2xl ${pillar.accent}`} />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-base font-semibold text-[#0f172a] leading-snug">{pillar.title}</h3>
                </div>
              </div>
              <div className="h-px md:h-10 w-full md:w-px bg-[#dbeafe] flex-shrink-0" />
              <p className="text-[#475569] text-sm leading-relaxed font-['Inter']">{pillar.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: "ri-layout-grid-line", title: "Modular Curriculum Architecture", desc: "Digitally structured learning pathways aligned to developmental economic cognition stages." },
            { icon: "ri-building-4-line", title: "Institutional Integration", desc: "Designed for adoption within school districts, nonprofit partnerships, and workforce readiness programs." },
            { icon: "ri-bar-chart-2-line", title: "Measurable Outcome Metrics", desc: "Data-driven assessment models tracking financial systems literacy progression and transition readiness." },
          ].map((item, i) => (
            <div key={i} className="bg-[#f8faff] border border-[#dbeafe] rounded-2xl p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#eff6ff] mb-4">
                <i className={`${item.icon} text-[#1d4ed8] text-lg`} />
              </div>
              <h4 className="font-['Playfair_Display'] text-sm font-semibold text-[#0f172a] mb-2">{item.title}</h4>
              <p className="text-[#475569] text-xs leading-relaxed font-['Inter']">{item.desc}</p>
            </div>
          ))}
        </div>


      </div>
    </section>
  );
}
